import Service, { inject as service } from '@ember/service';
import config from 'cio/config/environment';
import AlertsService from './alerts';
import { Editor } from 'cio/services/editor';

export class InAppStaticTemplate {
  id: string;
  name: string;
  path: string;
  thumbnail: string;
  ignore?: boolean;
  body?: string; // to store fetched content
  editor = Editor.VISUAL;

  constructor({
    id,
    name,
    path,
    thumbnail,
    ignore,
    body,
  }: {
    id: string;
    name: string;
    path: string;
    thumbnail: string;
    ignore?: boolean;
    body?: string;
  }) {
    this.id = id;
    this.name = name;
    this.path = path;
    this.thumbnail = thumbnail;

    if (ignore !== undefined) {
      this.ignore = ignore;
    }
    if (body !== undefined) {
      this.body = body;
    }
  }
}

interface Manifest {
  html: InAppStaticTemplate[];
}

const BUCKET_PROD = 'cio-in-app-templates';
const BUCKET_STANDALONE = 'cio-in-app-templates-standalone';

export default class InAppStaticTemplatesService extends Service {
  @service alerts!: AlertsService;

  private templates: Record<string, InAppStaticTemplate> = {};
  private manifest: Manifest | null = null;

  private get bucketUrl(): string {
    let base = 'https://storage.googleapis.com/';
    return base + (config.isProduction ? BUCKET_PROD : BUCKET_STANDALONE);
  }

  private get manifestUrl(): string {
    return `${this.bucketUrl}/manifest.json`;
  }

  async fetchTemplates(): Promise<InAppStaticTemplate[]> {
    await this.ensureAllTemplatesLoaded();
    return Object.values(this.templates).sort((a, b) => a.id.localeCompare(b.id));
  }

  private async fetchManifest(): Promise<void> {
    if (this.manifest != null) {
      return;
    }

    try {
      let response = await fetch(this.manifestUrl);
      let manifest = await response.json();
      let html = manifest.html.map(
        (template: InAppStaticTemplate) =>
          new InAppStaticTemplate({
            ...template,
            thumbnail: `${this.bucketUrl}/${template.thumbnail}`,
          }),
      );
      this.manifest = { html };
    } catch (e) {
      this.alerts.error('in_app.static_templates_manifest_fetch', 'Failed to fetch in-app templates manifest');
    }
  }

  private async fetchTemplateContent(template: InAppStaticTemplate): Promise<string> {
    try {
      let contentResponse = await fetch(`${this.bucketUrl}/html/${template.path}`);
      return contentResponse.text();
    } catch (e) {
      this.alerts.error(
        'in_app.static_templates_content_fetch',
        `Failed to fetch in-app template content for ${template.name}`,
      );
    }

    return '';
  }

  private async ensureAllTemplatesLoaded(): Promise<void> {
    await this.fetchManifest();
    let templates = this.manifest?.html;
    if (templates != null) {
      await Promise.all(
        templates.map((template) => {
          return (async () => {
            if (template.ignore === true) {
              return;
            }
            let content = await this.fetchTemplateContent(template);
            this.templates[template.id] = new InAppStaticTemplate({ ...template, body: content });
          })();
        }),
      );
    }
  }
}
